import { useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useEvents } from "@/hooks/useConflictData";
import { Plane, Lock, Zap, TrendingUp, TrendingDown, Minus, Search, ShieldAlert, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { calculateAirportRisks } from "@/utils/airportRisk";
import PricingDialog from "@/components/PricingDialog";

function getRiskColor(score: number) {
  if (score < 1) return "hsl(var(--success))";
  if (score < 2.5) return "hsl(var(--warning))";
  if (score < 4) return "hsl(var(--danger))";
  return "hsl(var(--destructive))";
}

function getRiskLabel(score: number) {
  if (score < 1) return "LOW";
  if (score < 2.5) return "MODERATE";
  if (score < 4) return "HIGH";
  return "EXTREME";
}

function getTrend(score: number) {
  if (score < 1.5) return <TrendingDown className="h-4 w-4 text-success" />;
  if (score < 3) return <Minus className="h-4 w-4 text-warning" />;
  return <TrendingUp className="h-4 w-4 text-destructive" />;
}

const RISK_LEVELS = ["ALL", "LOW", "MODERATE", "HIGH", "EXTREME"] as const;
type RiskLevel = (typeof RISK_LEVELS)[number];

const FlightRiskIndex = ({ embedded }: { embedded?: boolean }) => {
  const [pricingOpen, setPricingOpen] = useState(false);
  const { isPremium } = useAuth();
  const { data: events } = useEvents(undefined, isPremium);
  const [search, setSearch] = useState("");
  const [riskFilter, setRiskFilter] = useState<RiskLevel>("ALL");

  const airportRisks = useMemo(() => calculateAirportRisks(events), [events]);

  const filtered = useMemo(() => {
    let result = airportRisks;
    if (riskFilter !== "ALL") {
      result = result.filter((a) => getRiskLabel(a.risk) === riskFilter);
    }
    if (search) {
      result = result.filter(
        (a) =>
          a.code.toLowerCase().includes(search.toLowerCase()) ||
          a.city.toLowerCase().includes(search.toLowerCase()) ||
          a.country.toLowerCase().includes(search.toLowerCase())
      );
    }
    return result;
  }, [airportRisks, riskFilter, search]);

  if (!isPremium && !embedded) {
    return (
      <section className="border-b border-border">
        <div className="container mx-auto px-4 py-12">
          <div className="relative overflow-hidden rounded-xl border border-border bg-card p-8 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent" />
            <div className="relative">
              <Lock className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              <h2 className="mb-2 text-2xl font-bold text-foreground">✈️ Flight Risk Index</h2>
              <p className="mb-6 text-muted-foreground max-w-lg mx-auto">
                Real-time risk scores, airspace closure probability and airport status for conflict zones.
              </p>
              <Button
                onClick={() => setPricingOpen(true)}
                className="bg-primary text-primary-foreground"
              >
                <Zap className="mr-2 h-4 w-4" /> Unlock Flight Risk Index
              </Button>
            </div>
          </div>
        </div>
        <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
      </section>
    );
  }

  const Wrapper = embedded ? "div" : "section";
  const wrapperProps = embedded ? {} : { id: "flight-risk", className: "border-b border-border" };

  return (
    <Wrapper {...wrapperProps}>
      <div className={embedded ? "" : "container mx-auto px-4 py-8"}>
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Plane className="h-6 w-6 text-primary" /> Flight Risk Index
            </h2>
            <p className="text-sm text-muted-foreground">
              Risk score, airspace closure probability &amp; airport status
            </p>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search airport, city..."
              className="bg-background pl-9"
            />
          </div>
        </div>

        {/* Risk level filter */}
        <div className="mb-4 flex flex-wrap gap-2">
          {RISK_LEVELS.map((level) => {
            const isActive = riskFilter === level;
            const count = level === "ALL" ? airportRisks.length : airportRisks.filter((a) => getRiskLabel(a.risk) === level).length;
            return (
              <button
                key={level}
                onClick={() => setRiskFilter(level)}
                className={`rounded-full border px-3 py-1 text-xs font-mono font-bold transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-muted-foreground border-border hover:border-primary/50"
                }`}
              >
                {level} ({count})
              </button>
            );
          })}
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((airport) => (
            <div
              key={airport.code}
              className="rounded-lg border border-border bg-card p-4 hover:border-primary/30 transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-lg font-bold text-foreground">{airport.code}</span>
                  {getTrend(airport.risk)}
                </div>
                <Badge
                  className="font-mono text-xs"
                  style={{
                    background: `${getRiskColor(airport.risk)}20`,
                    color: getRiskColor(airport.risk),
                    borderColor: getRiskColor(airport.risk),
                  }}
                >
                  {getRiskLabel(airport.risk)}
                </Badge>
              </div>
              <p className="text-sm text-foreground">{airport.name}</p>
              <p className="text-xs text-muted-foreground">{airport.city}, {airport.country}</p>
              
              {/* Risk bar */}
              <div className="mt-3 flex items-center justify-between">
                <div className="flex-1">
                  <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${(airport.risk / 5) * 100}%`,
                        background: getRiskColor(airport.risk),
                      }}
                    />
                  </div>
                </div>
                <span className="ml-3 font-mono text-sm font-bold" style={{ color: getRiskColor(airport.risk) }}>
                  {airport.risk}
                </span>
              </div>

              {/* Airspace + Airport status row */}
              <div className="mt-3 grid grid-cols-2 gap-2">
                <div className="rounded-md border border-border bg-background px-2 py-1.5 text-center">
                  <div className="flex items-center justify-center gap-1 text-[10px] uppercase text-muted-foreground">
                    <ShieldAlert className="h-3 w-3" /> Airspace Risk
                  </div>
                  <div className="font-mono text-sm font-bold" style={{ color: airport.closureProb >= 50 ? "hsl(var(--danger))" : "hsl(var(--muted-foreground))" }}>
                    {airport.closureProb}%
                  </div>
                </div>
                <div className="rounded-md border border-border bg-background px-2 py-1.5 text-center">
                  <div className="flex items-center justify-center gap-1 text-[10px] uppercase text-muted-foreground">
                    <CheckCircle className="h-3 w-3" /> Status
                  </div>
                  <div className="font-mono text-xs font-bold" style={{ color: airport.status.color }}>
                    {airport.status.label}
                  </div>
                </div>
              </div>

              <p className="mt-2 text-xs text-muted-foreground font-mono">
                {airport.count} events nearby
              </p>
            </div>
          ))}
        </div>
      </div>
    </Wrapper>
  );
};

export default FlightRiskIndex;
